<!-- v-searchable-select aka vss -->
<template>
  <div :class="['relative', mounted && 'relative--mounted']">
    <portal to="search" :disabled="!fullscreen">
      <transition name="v-searchable-select--fullscreen">
        <div
          :class="[
            'v-searchable-select',
            small && 'v-searchable-select--small',
            gray && 'v-searchable-select--gray',
            search && 'v-searchable-select--search',
            url && 'v-searchable-select--async',
            url && showResults && 'v-searchable-select--show-results',
            showError && !!error && 'v-searchable-select--error',
            fullscreen && 'v-searchable-select--fullscreen',
            desktopFullscreen && 'v-searchable-select--desktopFullscreen',
            disabled && 'v-searchable-select--disabled'
          ]"
          :style="[minWidth && { minWidth: `${minWidth}px` }]"
          v-show="show"
          @keydown.esc="closeFullscreen()"
          @click.self="closeFullscreen()"
        >
          <div class="v-searchable-select__wrapper">
            <label :for="multiselectId" :id="labelId" class="sr-only">
              {{ getAccessibleLabel() }}
            </label>
            <multiselect
              :id="multiselectId"
              :aria-expanded="isExpanded ? 'true' : 'false'"
              :aria-controls="listboxId"
              :aria-labelledby="labelId"
              :aria-required="$attrs.required ? 'true' : 'false'"
              :aria-invalid="showError && !!error ? 'true' : 'false'"
              :aria-describedby="
                (showError && !!error) || description ? helperId : null
              "
              aria-autocomplete="list"
              role="combobox"
              v-model="selectedValue"
              v-bind="!multiple ? selectProps : multiselectProps"
              :options="internalOptions"
              :options-limit="2000"
              :max-height="400"
              :placeholder="placeholder"
              :label="label"
              :track-by="optionId"
              :group-values="getGroupValues"
              :group-label="getGroupLabel"
              :internalSearch="!url"
              :loading="loading"
              :option-height="optionHeight"
              :disabled="disabled"
              :multiple="multiple"
              @open="onOpen"
              @close="onClose"
              @input="onInput"
              @select="onSelect"
              @search-change="onSearchChange"
              spellcheck="false"
              ref="multiselect"
              :preserve-search="this.url ? true : false"
              :clear-on-select="this.url ? false : true"
            >
              <template #option="{option}">
                <template v-if="customResult && !option.$isLabel">
                  <slot v-bind:result="option" name="custom-result"></slot>
                </template>
                <span
                  v-else-if="option"
                  :class="[
                    'multiselect__standard-option',
                    option.locked && 'locked-icon'
                  ]"
                >
                  <strong v-if="option.locked && !metaLabel">
                    {{ getOptionLabel(option) }}
                    <v-icon name="lock" size="xxsmall" />
                  </strong>
                  <template v-else>
                    {{ getOptionLabel(option) }}
                  </template>
                  <span v-if="metaLabel">{{ option[metaLabel] }}</span>
                </span>
              </template>
              <template #caret="{toggle}">
                <div
                  v-show="!url && !fullscreen"
                  :class="['multiselect__caret', { 'mr-5': !!tooltip }]"
                  @mousedown.prevent.stop="toggle()"
                  role="button"
                  :aria-label="isExpanded ? 'Luk dropdown' : 'Åbn dropdown'"
                  tabindex="-1"
                >
                  <v-icon name="arrow-key-down" width="24" />
                </div>
                <div
                  v-show="search"
                  class="multiselect__search-icon"
                  aria-hidden="true"
                >
                  <v-icon name="search" width="22" />
                </div>
              </template>
              <template #noResult>
                <span class="multiselect__no-results" role="status">
                  <slot v-if="customNoResult" name="custom-no-result"></slot>
                  <template v-else>{{
                    $t(
                      "Your search returned no results",
                      "components.searchable-select.no-results"
                    )
                  }}</template>
                </span>
              </template>
              <template #loading>
                <div class="v-searchable-select__actions">
                  <v-loading
                    v-if="fetching"
                    :small="small"
                    :medium="!small"
                    role="status"
                    aria-label="Indlæser..."
                  ></v-loading>
                  <div
                    v-if="
                      (!fetching && query) ||
                        (!fetching && selectedValue && url) ||
                        (clearButton && selectedValue)
                    "
                    @click.prevent.stop="resetSearch()"
                    class="v-searchable-select__clear"
                    role="button"
                    aria-label="Ryd søgefelt"
                    tabindex="0"
                    @keydown.enter="resetSearch()"
                    @keydown.space.prevent="resetSearch()"
                  >
                    <div class="v-searchable-select__clear__icon">
                      <v-icon name="backspace" size="xsmall" />
                    </div>
                  </div>
                  <div
                    v-if="fullscreen"
                    class="v-searchable-select--fullscreen__close"
                    @mousedown.prevent.stop="closeFullscreen()"
                    role="button"
                    tabindex="0"
                    @keydown.enter="closeFullscreen()"
                    @keydown.space.prevent="closeFullscreen()"
                  >
                    {{ $t("Luk", "components.searchable-select.close") }}
                  </div>
                </div>
              </template>
              <template v-if="hasFallback" #afterList>
                <div class="v-searchable-select__after-list">
                  <slot
                    name="fallback"
                    :closeFullscreen="closeFullscreen"
                  ></slot>
                </div>
              </template>
            </multiselect>
            <div
              v-if="placeholder && !search"
              :class="[
                'v-searchable-select__wrapper__label',
                {
                  'v-searchable-select__wrapper__label--active':
                    selectedValue || address,
                  'v-searchable-select__wrapper__label--error': showError,
                  'v-searchable-select__wrapper__label__search':
                    search && !desktopFullscreen,
                  'v-searchable-select__wrapper__label__search__desktopFullscreen': desktopFullscreen
                }
              ]"
              aria-hidden="true"
            >
              <span
                :class="[
                  'v-searchable-select__wrapper__label__text',
                  {
                    'v-searchable-select__wrapper__label__text--active':
                      selectedValue || address
                  }
                ]"
              >
                {{ placeholder }}
              </span>
            </div>
            <span
              v-if="address && !selectedValue"
              :class="['v-searchable-select__wrapper__address-value']"
              aria-hidden="true"
            >
              {{ formatAddress }}</span
            >
          </div>
          <input
            class="v-searchable-select__input"
            type="text"
            :value="selectedValue && getSelectedValue()"
            v-bind="$attrs"
            @invalid="onInvalid"
            ref="input"
            :id="valueInputId"
            tabindex="-1"
            aria-hidden="true"
            autocomplete="off"
          />
          <div
            v-if="showError || description"
            class="v-searchable-select__helper"
            :id="helperId"
            :role="showError ? 'alert' : null"
          >
            {{ showError ? error : description }}
          </div>
        </div>
      </transition>
    </portal>
    <!-- Use shallow component on mobile to trigger fullscreen view -->
    <v-searchable-select-shallow
      v-if="mobile && !desktopFullscreen"
      :placeholder="placeholder"
      :label="label"
      :name="$attrs['name']"
      :required="$attrs.required"
      :value="selectedValue"
      @input="newVal => (selectedValue = newVal)"
      :async="!!url"
      :small="small"
      :gray="gray"
      :search="search"
      :error="error"
      :show-error="showError"
      @click="openFullscreen"
      :disabled="disabled"
      :tooltip="tooltip"
      :address="formatAddress"
      :multiple="multiple"
    />
    <v-infoicon v-if="tooltip" :tooltip="tooltip" offset="16px"></v-infoicon>
  </div>
</template>

<script>
// Dependencies
import Multiselect from "vue-multiselect";
import vSearchableSelectShallow from "./v-searchable-select-shallow";

// Mixins
import { default as resetMixin } from "../v-form/mixin.reset";
import { default as propsMixin } from "./mixins/props";
import { default as dataMixin } from "./mixins/data";
import { default as eventsMixin } from "./mixins/events";
import { default as searchMixin } from "./mixins/search";
import { default as mobileMixin } from "./mixins/mobile";

export default {
  inheritAttrs: false,
  mixins: [
    resetMixin,
    propsMixin,
    dataMixin,
    eventsMixin,
    searchMixin,
    mobileMixin
  ],
  components: {
    Multiselect,
    "v-searchable-select-shallow": vSearchableSelectShallow
  },
  watch: {
    value(value) {
      this.selectedValue = this.transformValue(value);
      if ((value && value.length == 0) || value == null) {
        this.selectedValue = null;
      }
    },
    getUrl() {
      this.updateUrl(this.url);
      this.clearSelectedValue();
    }
  },
  created() {
    this.showError = this.invalid !== false;
  },
  mounted() {
    // Fix accessibility attributes immediately and after Vue updates
    this.$nextTick(() => {
      this.fixInputElement();
    });

    if (this.desktopFullscreen) {
      this.$root.$on(this.fullscreenTrigger, this.openFullscreen);
    }
  },
  updated() {
    // Ensure accessibility attributes persist after updates
    this.$nextTick(() => {
      this.fixInputElement();
    });
  },
  beforeDestroy() {
    if (this.desktopFullscreen) {
      this.$root.$off(this.fullscreenTrigger, this.openFullscreen);
    }
  },
  computed: {
    hasFallback() {
      return !!this.$slots.fallback;
    },
    getUrl() {
      return this.url;
    },
    isManualId() {
      return !!this.$attrs.id;
    },
    multiselectId() {
      return this.isManualId ? this.uniqId : `multiselect-${this.uniqId}`;
    },
    labelId() {
      return this.isManualId
        ? `${this.uniqId}-label`
        : `multiselect-label-${this.uniqId}`;
    },
    helperId() {
      return this.isManualId
        ? `${this.uniqId}-helper`
        : `multiselect-helper-${this.uniqId}`;
    },
    listboxId() {
      return this.isManualId
        ? `${this.uniqId}-listbox`
        : `listbox-${this.uniqId}`;
    },
    valueInputId() {
      return this.isManualId
        ? `${this.uniqId}-value`
        : `searchable-select-value-${this.uniqId}`;
    }
  },
  data() {
    return {
      isExpanded: false
    };
  },
  methods: {
    onOpen() {
      this.isExpanded = true;
      this.$emit("open");
    },
    onClose() {
      this.isExpanded = false;
      this.$emit("close");
    },
    openFullscreen() {
      setTimeout(() => this.multiselect().search.focus(), 0);
      if (this.mobile) this.openMobile();
      this.fullscreenOpen = true;
    },
    closeFullscreen() {
      setTimeout(() => this.multiselect().search.blur(), 0);
      if (this.mobile) this.closeMobile();
      this.fullscreenOpen = false;
    },
    getSelectedValue() {
      return this.selectedValue.value || this.selectedValue;
    },
    transformValue(value) {
      if (this.simpleSelect) {
        return this.options.find(option => option.value == value);
      } else {
        return value;
      }
    },
    getOptionLabel(option) {
      if (option.$isLabel) return option.$groupLabel;
      return option[this.label] || option || "";
    },
    getAccessibleLabel() {
      // Create a more descriptive label for screen readers
      const baseLabel = this.placeholder || "Vælg en mulighed";
      const requiredText = this.$attrs.required ? " (påkrævet)" : "";
      const multipleText = this.multiple ? " (flere valg mulige)" : "";
      return baseLabel + requiredText + multipleText;
    },
    fixInputElement() {
      const multiselectRefs = this.multiselect();
      if (!multiselectRefs || !multiselectRefs.search) return;

      const inputElement = multiselectRefs.search;

      // Set autocorrect, autocapitalize, and autocomplete
      inputElement.setAttribute("autocorrect", "off");
      inputElement.setAttribute("autocapitalize", "off");
      inputElement.setAttribute("autocomplete", "off");

      // Set aria-labelledby to reference the label we created
      inputElement.setAttribute("aria-labelledby", this.labelId);

      // Add role if not present
      if (!inputElement.getAttribute("role")) {
        inputElement.setAttribute("role", "searchbox");
      }

      // Add aria-required if needed
      if (this.$attrs.required) {
        inputElement.setAttribute("aria-required", "true");
      }

      // Add aria-invalid if there's an error
      if (this.showError && this.error) {
        inputElement.setAttribute("aria-invalid", "true");
        inputElement.setAttribute("aria-describedby", this.helperId);
      }

      // DO NOT add name attribute to the search input - it would cause duplicate form submission
      // The hidden input element (ref="input") already has the name attribute via v-bind="$attrs"

      // Add aria-controls for the dropdown list
      inputElement.setAttribute("aria-controls", this.listboxId);

      // Add aria-expanded
      inputElement.setAttribute(
        "aria-expanded",
        this.isExpanded ? "true" : "false"
      );
    },
    deactivate() {
      if (this.fullscreen) this.closeFullscreen();
      this.$refs.multiselect.deactivate();
    },
    triggerSearch(query) {
      this.$refs.multiselect.updateSearch(query);
    },
    updateUrl(url) {
      this.internalUrl = url;
    },
    setSearchDisabled(value) {
      this.searchDisabled = value;
    },
    multiselect() {
      return this.$refs.multiselect ? this.$refs.multiselect.$refs : null;
    },
    clearSelectedValue() {
      this.selectedValue = null;
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped lang="scss">
@import "../../sass/component.scss";

.relative {
  position: relative;
  visibility: hidden;

  &--mounted {
    visibility: visible;
  }
}

.v-searchable-select {
  &__wrapper {
    position: relative;

    &__address-value {
      display: inline-block;
      position: absolute;
      top: 13px;
      left: 10px;
      right: auto;
      bottom: auto;
      max-width: 100%;
      color: theme-color("black");
      background: transparent;
      padding: 0 5px;
      transform-origin: left top;
      transition: all 200ms ease-out;
      cursor: text;
      pointer-events: none;
      white-space: nowrap;
    }

    @include media-breakpoint-up(md) {
      &__label {
        display: inline-block;
        position: absolute;
        top: 13px;
        left: 10px;
        right: auto;
        bottom: auto;
        max-width: 100%;
        color: theme-color("placeholder");
        background: transparent;
        padding: 0 5px;
        transform-origin: left top;
        transition: all 200ms ease-out;
        cursor: text;
        pointer-events: none;
        white-space: nowrap;
        line-height: 24px;

        &__search {
          top: 16px;
          left: $spacer * 3;

          .multiselect--active + & .v-searchable-select__wrapper__label__text {
            position: relative;
            top: -12px;

            &--error {
              color: theme-color("danger");
            }
          }

          &__text {
            &--active {
              position: relative;
              top: -12px;
            }
          }

          &__desktopFullscreen {
            top: 26px;
            left: $spacer * 3;
          }
        }

        .multiselect--active + & .v-searchable-select__wrapper__label__text {
          position: relative;
          top: -12px;

          &--error {
            color: theme-color("danger");
          }
        }

        &__text {
          &--active {
            position: relative;
            top: -12px;
          }
        }

        .multiselect--active + & {
          background-color: theme-color("white");
          top: 0px;
          z-index: 49;
          font-size: 0.8125rem;
          color: theme-color("focus");
          height: 5px;

          &--error {
            color: theme-color("danger");
          }
        }

        &__search__desktopFullscreen {
          display: none;
        }
        &--active {
          background-color: theme-color("white");
          top: 0px;
          z-index: 47;
          font-size: 0.8125rem;
          height: 5px;
        }

        &--error {
          color: theme-color("danger");
        }

        .v-searchable-select--gray & {
          background-color: inherit;
        }
      }
    }
  }
  &__input {
    display: none;
  }

  &__close {
    display: none;
  }

  &__clear {
    position: absolute;
    right: 5px;
    top: 3px;
    padding: 12px 10px 10px 10px;
    z-index: 10;
    background-color: theme-color("white");

    .v-searchable-select--fullscreen & {
      right: 50px;
    }

    &__icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      opacity: 0.8;
      cursor: pointer;
      transition: opacity 200ms ease-out;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  &__helper {
    display: none;

    @include media-breakpoint-up(md) {
      display: block;
      margin-top: 5px;
      font-size: 0.75rem;
      color: theme-color("placeholder");
    }
  }

  &__after-list {
    position: sticky;
    bottom: 0;
    background-color: theme-color("white");
    padding: 11px 0;
    border-top: 1px solid #ddd;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &--disabled {
    cursor: not-allowed;
  }
}

// Multiselect deep styling for different component states
@import "./styles/base.scss";
@import "./styles/small.scss";
@import "./styles/gray.scss";
@import "./styles/search.scss";
@import "./styles/async.scss";
@import "./styles/error.scss";
@import "./styles/fullscreen.scss";
</style>
